import {
  BrandStatusSeverity,
  BrandStatusType,
  FeatureFlags,
  type BrandStatus,
  type IntegrationKey,
  type IntegrationPartnerProgress,
  type RecordEntityId,
} from '@centric-os/types';
import type { State } from '.';
import { get, isEmpty, isNil } from 'lodash-es';
import { useSplitIoStore } from '@centric-os/stores/splitio/use-split-io';
import { DateTime } from 'luxon';

/**
 * Gets status severities for a specific integration partner in a brand.
 *
 * @param {State} state - The application state with brand information.
 * @param {IntegrationKey} partner - The integration to check.
 * @returns {BrandStatusSeverity[]} - Array of status severities for the partner.
 *
 * @description
 * Finds all status severities related to a specific integration partner from a brand's statuses.
 * Returns an empty array if the partner or integration data doesn't exist.
 *
 * @example
 * const statuses = getPartnerStatuses(state, 'nextep');
 * // Returns: [BrandStatusSeverity.OK, BrandStatusSeverity.ERROR]
 */

const getPartnerStatuses = (state: State, partner: IntegrationKey): BrandStatusSeverity[] => {
  const progress = state.brand?.statuses?.find(
    ({ type }) => type === BrandStatusType.UPDATE_INTEGRATED_PARTNERS,
  )?.metadata?.progress as IntegrationPartnerProgress;

  if (!progress) return [];

  const partnerProgress: Record<RecordEntityId, Record<RecordEntityId, BrandStatusSeverity>> = get(
    progress,
    partner,
  );
  if (!partnerProgress) return [];

  return Object.values(partnerProgress).flatMap(
    (menuStatus: Record<RecordEntityId, BrandStatusSeverity>) => Object.values(menuStatus),
  );
};

export const getters = {
  isBrandV4: (state: State): boolean => {
    return !isNil(state.brand?.catalog_id);
  },
  isLocalBrand: (state: State): boolean => {
    return !!state.brand?.local_menu_group_id;
  },
  isLocalBrandV4: (state: State): boolean => {
    return !!(state.brand?.local_menu_group_id && state.brand?.catalog_id);
  },
  isLocalOnlyV4Brand: (state: State): boolean => {
    return !!(
      state.brand?.local_menu_group_id &&
      state.brand?.catalog_id &&
      !state.brand?.parent_id
    );
  },
  isImportingBrandExcel: (state: State): boolean => {
    const importingBrandExcelStatus: BrandStatus = state.brand?.statuses?.find(
      (el) =>
        el.status === BrandStatusSeverity.IN_PROGRESS &&
        el.type === BrandStatusType.IMPORT_BRAND_EXCEL,
    );

    return Boolean(importingBrandExcelStatus);
  },
  isImportingBrandExcelComplete: (state: State): boolean => {
    const importingBrandExcelStatusComplete: BrandStatus = state.brand?.statuses?.find(
      (el) =>
        el.status === BrandStatusSeverity.OK && el.type === BrandStatusType.IMPORT_BRAND_EXCEL,
    );

    return Boolean(importingBrandExcelStatusComplete);
  },
  isImportingBrandExcelFailed: (state: State): boolean => {
    const importingBrandExcelStatusFailed: BrandStatus = state.brand?.statuses?.find(
      (el) =>
        el.status === BrandStatusSeverity.ERROR && el.type === BrandStatusType.IMPORT_BRAND_EXCEL,
    );

    return Boolean(importingBrandExcelStatusFailed);
  },
  isApplyingChanges: (state: State): boolean => {
    const applyingChangesStatus: BrandStatus = state.brand?.statuses?.find(
      (el) =>
        el.status === BrandStatusSeverity.IN_PROGRESS && el.type === BrandStatusType.APPLY_DIFF,
    );

    return Boolean(applyingChangesStatus);
  },
  isApplyingChangesFailed: (state: State): boolean => {
    const applyingChangesStatusError: BrandStatus = state.brand?.statuses?.find(
      (el) => el.status === BrandStatusSeverity.ERROR && el.type === BrandStatusType.APPLY_DIFF,
    );

    return Boolean(applyingChangesStatusError);
  },
  isApplyingChangesComplete: (state: State): boolean => {
    const applyingChangesStatusComplete: BrandStatus = state.brand?.statuses?.find(
      (el) => el.status === BrandStatusSeverity.OK && el.type === BrandStatusType.APPLY_DIFF,
    );

    return Boolean(applyingChangesStatusComplete);
  },
  isSyncingMenuWorks: (state: State): boolean => {
    const syncingMenuWorksStatus: BrandStatus = state.brand?.statuses?.find(
      (el) =>
        (el.status === BrandStatusSeverity.INITIATED ||
          el.status === BrandStatusSeverity.IN_PROGRESS) &&
        el.type === BrandStatusType.MENUWORKS_SYNC,
    );

    if (!syncingMenuWorksStatus) return false;

    // Return true if the sync has not timed out after 15 minutes
    const createdAt: DateTime = DateTime.fromISO(syncingMenuWorksStatus.created_at as string);
    const minutesSinceTriggered: number = DateTime.utc().diff(createdAt, 'minutes').minutes;
    const timeoutThreshold: number = 15;

    return minutesSinceTriggered <= timeoutThreshold;
  },
  isMenuWorksSyncComplete: (state: State): boolean => {
    const syncingMenuWorksStatusComplete: BrandStatus = state.brand?.statuses?.find(
      (el) => el.status === BrandStatusSeverity.OK && el.type === BrandStatusType.MENUWORKS_SYNC,
    );

    return Boolean(syncingMenuWorksStatusComplete);
  },
  hasMenuWorksSyncFailed: (state: State): boolean => {
    const syncingMenuWorksStatusFailed: BrandStatus = state.brand?.statuses?.find(
      (el) => el.status === BrandStatusSeverity.ERROR && el.type === BrandStatusType.MENUWORKS_SYNC,
    );

    return Boolean(syncingMenuWorksStatusFailed);
  },
  hasMenuWorksSyncTimeOut: (state: State): boolean => {
    const staleSyncStatus: BrandStatus = state.brand?.statuses?.find(
      (el) =>
        (el.status === BrandStatusSeverity.INITIATED ||
          el.status === BrandStatusSeverity.IN_PROGRESS) &&
        el.type === BrandStatusType.MENUWORKS_SYNC,
    );

    if (!staleSyncStatus) return false;

    const createdAt: DateTime = DateTime.fromISO(staleSyncStatus.created_at as string);
    const minutesSinceTriggered: number = DateTime.utc().diff(createdAt, 'minutes').minutes;
    const timeoutThreshold: number = 15;

    return minutesSinceTriggered > timeoutThreshold;
  },
  hasIntegratedPartners: (state: State): boolean => {
    return Boolean(
      state.brand?.statuses?.some(
        (s) =>
          s.type === BrandStatusType.UPDATE_INTEGRATED_PARTNERS && !isEmpty(s.metadata?.progress),
      ),
    );
  },
  isImportingMenuWorks: (state: State): boolean => {
    return Boolean(
      state.brand?.statuses?.find(
        (el) =>
          (el.status === BrandStatusSeverity.INITIATED ||
            el.status === BrandStatusSeverity.IN_PROGRESS) &&
          el.type === BrandStatusType.MENUWORKS_IMPORT,
      ),
    );
  },
  isMenuWorksImportComplete: (state: State): boolean => {
    const importMenuWorksStatusComplete: BrandStatus = state.brand?.statuses?.find(
      (el) => el.status === BrandStatusSeverity.OK && el.type === BrandStatusType.MENUWORKS_IMPORT,
    );

    return Boolean(importMenuWorksStatusComplete);
  },
  publishingStatus: (state: State): boolean => {
    const publishingStatus: BrandStatus = state.brand?.statuses?.find(
      (el) =>
        (el.status === BrandStatusSeverity.IN_PROGRESS ||
          el.status === BrandStatusSeverity.INITIATED) &&
        el.type === BrandStatusType.PUBLISH,
    );

    return Boolean(publishingStatus);
  },
  isPublishingComplete: (state: State): boolean => {
    const publishingComplete: BrandStatus = state.brand?.statuses?.find(
      (el) => el.status === BrandStatusSeverity.OK && el.type === BrandStatusType.PUBLISH,
    );

    return Boolean(publishingComplete);
  },
  hasPublishingTimedOut: (state: State): boolean => {
    const publishingTimedOut: BrandStatus = state.brand?.statuses?.find(
      (el) => el.status === BrandStatusSeverity.TIMEOUT && el.type === BrandStatusType.PUBLISH,
    );

    return Boolean(publishingTimedOut);
  },
  isPublishingNextepComplete: (state: State): boolean => {
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'nextep');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.every((status) => status === BrandStatusSeverity.OK);
  },
  isPublishingToNextep: (state: State): boolean => {
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'nextep');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return (
      !allStatuses.includes(BrandStatusSeverity.ERROR) &&
      !allStatuses.includes(BrandStatusSeverity.TIMEOUT) &&
      !allStatuses.every((status) => status === BrandStatusSeverity.OK)
    );
  },
  hasPublishingNextepFailed: (state: State): boolean => {
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'nextep');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.some((status) => status === BrandStatusSeverity.ERROR);
  },
  hasPublishingNextepTimedOut: (state: State): boolean => {
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'nextep');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.includes(BrandStatusSeverity.TIMEOUT);
  },
  isPublishingIllumiaComplete: (state: State): boolean => {
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'illumia');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.every((status) => status === BrandStatusSeverity.OK);
  },
  isPublishingToIllumia: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_ILLUMIA)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'illumia');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return (
      !allStatuses.includes(BrandStatusSeverity.ERROR) &&
      !allStatuses.includes(BrandStatusSeverity.TIMEOUT) &&
      !allStatuses.every((status) => status === BrandStatusSeverity.OK)
    );
  },
  hasPublishingIllumiaFailed: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_ILLUMIA)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'illumia');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.some((status) => status === BrandStatusSeverity.ERROR);
  },
  hasPublishingIllumiaTimedOut: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_ILLUMIA)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'illumia');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.includes(BrandStatusSeverity.TIMEOUT);
  },
  isPublishingAmazonComplete: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_AMAZON)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'amazon');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.every((status) => status === BrandStatusSeverity.OK);
  },
  isPublishingToAmazon: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_AMAZON)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'amazon');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return (
      !allStatuses.includes(BrandStatusSeverity.ERROR) &&
      !allStatuses.includes(BrandStatusSeverity.TIMEOUT) &&
      !allStatuses.every((status) => status === BrandStatusSeverity.OK)
    );
  },
  hasPublishingAmazonFailed: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_AMAZON)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'amazon');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.some((status) => status === BrandStatusSeverity.ERROR);
  },
  hasPublishingAmazonTimedOut: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_AMAZON)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'amazon');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.includes(BrandStatusSeverity.TIMEOUT);
  },
  isPublishingGrubhubComplete: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_GRUB_HUB)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'grubhub');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.every((status) => status === BrandStatusSeverity.OK);
  },
  isPublishingToGrubhub: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_GRUB_HUB)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'grubhub');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return (
      !allStatuses.includes(BrandStatusSeverity.ERROR) &&
      !allStatuses.includes(BrandStatusSeverity.TIMEOUT) &&
      !allStatuses.every((status) => status === BrandStatusSeverity.OK)
    );
  },
  hasPublishingGrubhubFailed: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_GRUB_HUB)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'grubhub');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.some((status) => status === BrandStatusSeverity.ERROR);
  },
  hasPublishingGrubhubTimedOut: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_GRUB_HUB)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'grubhub');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.includes(BrandStatusSeverity.TIMEOUT);
  },
  isPublishingVolanteComplete: (state: State): boolean => {
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'volante');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.every((status) => status === BrandStatusSeverity.OK);
  },
  isPublishingToVolante: (state: State): boolean => {
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'volante');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return (
      !allStatuses.includes(BrandStatusSeverity.ERROR) &&
      !allStatuses.includes(BrandStatusSeverity.TIMEOUT) &&
      !allStatuses.every((status) => status === BrandStatusSeverity.OK)
    );
  },
  hasPublishingVolanteFailed: (state: State): boolean => {
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'volante');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.some((status) => status === BrandStatusSeverity.ERROR);
  },
  hasPublishingVolanteTimedOut: (state: State): boolean => {
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'volante');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.includes(BrandStatusSeverity.TIMEOUT);
  },
  isPublishingAgilysysComplete: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_AGILYSYS)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'agilysys');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.every((status) => status === BrandStatusSeverity.OK);
  },
  isPublishingToAgilysys: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_AGILYSYS)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'agilysys');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return (
      !allStatuses.includes(BrandStatusSeverity.ERROR) &&
      !allStatuses.includes(BrandStatusSeverity.TIMEOUT) &&
      !allStatuses.every((status) => status === BrandStatusSeverity.OK)
    );
  },
  hasPublishingAgilysysFailed: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_AGILYSYS)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'agilysys');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.some((status) => status === BrandStatusSeverity.ERROR);
  },
  hasPublishingAgilysysTimedOut: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_AGILYSYS)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'agilysys');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.includes(BrandStatusSeverity.TIMEOUT);
  },
  isPublishingToAgilysysDod: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_AGILYSYS_DOD)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'agilysys_dod');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return (
      !allStatuses.includes(BrandStatusSeverity.ERROR) &&
      !allStatuses.includes(BrandStatusSeverity.TIMEOUT) &&
      !allStatuses.every((status) => status === BrandStatusSeverity.OK)
    );
  },
  hasPublishingAgilysysDodFailed: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_AGILYSYS_DOD)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'agilysys_dod');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.includes(BrandStatusSeverity.ERROR);
  },
  hasPublishingAgilysysDodTimedOut: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_AGILYSYS_DOD)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'agilysys_dod');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.includes(BrandStatusSeverity.TIMEOUT);
  },
  isPublishingTime2EatComplete: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_TIME2EAT)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'time2eat');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.every((status) => status === BrandStatusSeverity.OK);
  },
  isPublishingToTime2Eat: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_TIME2EAT)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'time2eat');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return (
      !allStatuses.includes(BrandStatusSeverity.ERROR) &&
      !allStatuses.includes(BrandStatusSeverity.TIMEOUT) &&
      !allStatuses.every((status) => status === BrandStatusSeverity.OK)
    );
  },
  hasPublishingTime2EatFailed: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_TIME2EAT)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'time2eat');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.some((status) => status === BrandStatusSeverity.ERROR);
  },
  hasPublishingTime2EatTimedOut: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_TIME2EAT)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'time2eat');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.includes(BrandStatusSeverity.TIMEOUT);
  },
  isPublishingMashginComplete: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_MASHGIN)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'mashgin');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.every((status) => status === BrandStatusSeverity.OK);
  },
  isPublishingToMashgin: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_MASHGIN)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'mashgin');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return (
      !allStatuses.includes(BrandStatusSeverity.ERROR) &&
      !allStatuses.includes(BrandStatusSeverity.TIMEOUT) &&
      !allStatuses.every((status) => status === BrandStatusSeverity.OK)
    );
  },
  hasPublishingMashginFailed: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_MASHGIN)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'mashgin');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.some((status) => status === BrandStatusSeverity.ERROR);
  },
  hasPublishingMashginTimedOut: (state: State): boolean => {
    const { isFeatureOn } = useSplitIoStore();
    if (!isFeatureOn(FeatureFlags.MENU_STATUS_MASHGIN)) {
      return false;
    }
    const allStatuses: BrandStatusSeverity[] = getPartnerStatuses(state, 'mashgin');
    if (isEmpty(allStatuses)) {
      return false;
    }

    return allStatuses.includes(BrandStatusSeverity.TIMEOUT);
  },
};
